import { useInfiniteQuery, useQuery, type UseInfiniteQueryResult } from '@tanstack/react-query';
import type { ChannelPublic, Page, RankedVideoRef, VideoSummary } from '@streamr/shared';
import { api } from '../client';
import { routes } from '../routes';

/**
 * Every list in the API pages identically -- `?limit&cursor` in,
 * `{items,nextCursor}` out (foundation contract 3/5) -- so one helper covers
 * the feed, search, channel videos, comments and notifications.
 */
export const usePagedQuery = <T>(
  key: readonly unknown[],
  path: string,
  query: Record<string, string | number | undefined> = {},
  options: { enabled?: boolean; limit?: number } = {},
): UseInfiniteQueryResult<{ pages: Page<T>[]; pageParams: unknown[] }, Error> =>
  useInfiniteQuery({
    queryKey: key,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam, signal }) =>
      api.get<Page<T>>(path, {
        query: { ...query, limit: options.limit ?? 24, cursor: pageParam },
        signal,
      }),
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: options.enabled ?? true,
  });

/** Flattens the pages a component actually renders. */
export const flattenPages = <T>(data: { pages: Page<T>[] } | undefined): T[] =>
  data?.pages.flatMap((page) => page.items) ?? [];

export const useFeed = (tag?: string) =>
  usePagedQuery<VideoSummary>(['feed', tag ?? null], routes.discovery.feed(), { tag });

export const useSearch = (q: string) =>
  usePagedQuery<VideoSummary>(['search', q], routes.discovery.search(), { q }, { enabled: q.trim() !== '' });

export const useChannel = (handle: string) =>
  useQuery({
    queryKey: ['channel', handle],
    queryFn: ({ signal }) => api.get<ChannelPublic>(routes.channels.byHandle(handle), { signal }),
    enabled: handle !== '',
  });

export const useChannelVideos = (handle: string, sort: 'recent' | 'popular' = 'recent') =>
  usePagedQuery<VideoSummary>(
    ['channel-videos', handle, sort],
    routes.channels.videos(handle),
    { sort },
    { enabled: handle !== '' },
  );

/**
 * "Up next" is a plain list, not paged: the ranker returns a fixed small set
 * (upNextQuerySchema caps limit at 20) and an endless recommendation rail is
 * not a v1 goal.
 *
 * The wire shape is NOT a bare VideoSummary array: each item wraps the summary
 * as `{ video, score, reason }` (upNextResponseSchema), so the video the card
 * renders is `item.video`, not `item`. Typing it flat here is exactly what let
 * WatchPage read `item.channel` and white-screen the page.
 */
export interface UpNextItem {
  video: VideoSummary;
  score: number;
  reason: RankedVideoRef['reason'];
}

export const useUpNext = (videoId: string, limit = 10) =>
  useQuery({
    queryKey: ['up-next', videoId, limit],
    queryFn: ({ signal }) =>
      api.get<{ items: UpNextItem[]; ranker: string }>(routes.videos.upNext(videoId), {
        query: { limit },
        signal,
      }),
    enabled: videoId !== '',
  });
